import { useSelector } from 'react-redux';
import React, { useState, useEffect, Fragment } from 'react';

import { Grid, Typography } from '@mui/material';

import { snackAlertData } from 'utils/constants';
import DateRangeFilter from './watchListFilters';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { useInvoicesService } from 'store/services/invoices.service';
import { watchlistTableHeaders } from '../../utils/watchlistTableHeaders';
import { DataTableWithAccordion, TablePagination, CircleLoading, SnackBarAlert } from 'shared';

const WatchlistTab = () => {
    const { getWatchLists } = useInvoicesService();
    const companyId = useSelector((state) => state?.company?.data?.id);

    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [sortOrder, setSortOrder] = useState('desc');
    const [filterValues, setFilterValues] = useState({});
    const [tableLoading, setTableLoading] = useState(true);
    const [sorting, setSorting] = useState({ date: true });
    const [alertData, setAlertData] = useState(snackAlertData);

    const handleGetWatchLists = async (page, filters = {}, column = null, sortOrder = null) => {
        try {
            setTableLoading(true);
            const params = {
                companyId,
                page,
                sortColumn: column,
                sortOrder,
                ...filters,
            };

            const response = await getWatchLists(params);
            setData(response);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setTableLoading(false);
        }
    };

    const handleFiltersSubmit = async (values) => {
        try {
            setPage(1);
            setFilterValues(values);
            await handleGetWatchLists(1, values, Object.keys(sorting)[0], sortOrder);
        } catch (error) {
            console.error('Error applying filters:', error);
        }
    };

    const handlePageChange = async (pageNumber) => {
        setPage(pageNumber);
        await handleGetWatchLists(pageNumber, filterValues, Object.keys(sorting)[0], sortOrder);
    };

    const sortHandler = async (columnId, newSortOrder) => {
        setSorting({ [columnId]: true });
        setSortOrder(newSortOrder);
        handleGetWatchLists(page, filterValues, columnId, newSortOrder);
    };

    useEffect(() => {
        if (companyId) {
            handleGetWatchLists(page, {}, Object.keys(sorting)[0], sortOrder);
        }
    }, [companyId]);

    return (
        <Fragment>
            {!data ? (
                <CircleLoading />
            ) : (
                <Grid container sx={{ p: 3 }}>
                    <Grid item xs={12} sx={{ mb: 3 }}>
                        <DateRangeFilter
                            onFilterChange={handleFiltersSubmit}
                            initialValues={filterValues}
                        />
                    </Grid>

                    {data?.count > 0 && (
                        <Grid
                            item
                            xs={12}
                            display="flex"
                            marginBottom="5px"
                            justifyContent="flex-end"
                        >
                            <Typography fontSize="18px">{`TOTAL: ${data?.count} ROW${
                                data?.count > 1 ? 'S' : ''
                            }`}</Typography>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <DataTableWithAccordion
                            data={data}
                            sorting={sorting}
                            sortOrder={sortOrder}
                            isLoading={tableLoading}
                            headers={watchlistTableHeaders}
                            sortHandler={(columnId, newSortOrder) =>
                                sortHandler(columnId, newSortOrder)
                            }
                        />
                    </Grid>

                    <Grid item xs={12} p={'10px 0'}>
                        <TablePagination
                            page={page}
                            count={data?.count || 0}
                            onChange={(e, pageNumber) => handlePageChange(pageNumber)}
                        />
                    </Grid>

                    <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
                </Grid>
            )}
        </Fragment>
    );
};

export default WatchlistTab;
